/**
 * Dates as people read them.
 *
 * `src/engine/` computes with `IsoDate`/`Minutes`; this turns that arithmetic
 * into words. Kept out of the engine on purpose, and out of `src/ui/format.ts`
 * too — Station Ledger has no date formatting at all (`formatTime` is the
 * only clock-shaped thing it exports), because the calculator it comes from
 * has no dates to format. This file is the missing layer for ShiftSwap.
 *
 * The weekday and month names are hand-rolled arrays rather than
 * `Intl.DateTimeFormat`, on purpose: `Intl.DateTimeFormat` would drag in a
 * timezone this app deliberately does not have. Every time in ShiftSwap is
 * ACT wall-clock and none of it ever converts — see CLAUDE.md's "Dates are
 * IsoDate strings" convention and `src/engine/civil.ts`'s header. The one
 * documented exception, `hasShiftStarted`, needs a real instant and says so;
 * nothing here does.
 */

import { dayOfWeek, parseIsoDate } from '../engine/civil'
import type { IsoDate, Minutes } from '../engine/types'
import { formatTime } from '../ui/format'

/** Indexed by `dayOfWeek()` — 0 = Sunday. */
export const WEEKDAY_SHORT: readonly string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** Indexed by `month - 1` — `parseIsoDate`'s `month` is 1–12. */
export const MONTH_LONG: readonly string[] = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/** 'Tue 26 May' — the roster's own way of naming a day. */
export function formatShortDate(date: IsoDate): string {
  const { month, day } = parseIsoDate(date)
  return `${WEEKDAY_SHORT[dayOfWeek(date)]} ${day} ${MONTH_LONG[month - 1].slice(0, 3)}`
}

/**
 * '06:30–16:30' — an EN DASH (U+2013), not a hyphen. Station Ledger's
 * `format.ts` exports `MINUS` (U+2212) for figures, but nothing for a range;
 * `ShiftRow`'s docstring establishes the en dash as the house format, so it
 * is written out literally here rather than borrowed from anywhere else.
 */
export function formatTimeRange(startMin: Minutes, endMin: Minutes): string {
  return `${formatTime(startMin)}–${formatTime(endMin)}`
}
